import { useEffect, useRef, createContext, useContext } from "react";
import type { ReactNode } from "react";
import MsAuthenticationService from "@/common/services/MsAuthenticationService";

interface AzureAuthContextType {
  authService: MsAuthenticationService | null;
  getEntraToken: () => Promise<string | null>;
  startLogin: () => Promise<void>;
}

const AzureAuthContext = createContext<AzureAuthContextType | null>(null);

export const useAzureAuth = () => {
  const context = useContext(AzureAuthContext);
  if (!context) {
    throw new Error("useAzureAuth must be used within AzureAuthProvider");
  }
  return context;
};

interface AzureAuthProviderProps {
  children: ReactNode;
  authService: MsAuthenticationService;
  triggerLoginOnMount?: boolean;
}

/**
 * AzureAuthProvider is a reusable component that handles Azure AD authentication for internal bots.
 * It manages the full login flow including popup/redirect authentication and provides
 * authentication context to child components.
 * 
 * By default, it only initializes the auth service but doesn't trigger login automatically.
 * Login is triggered when startLogin is called or when getEntraToken is called and user needs to authenticate.
 * 
 * Usage:
 * ```tsx
 * <AzureAuthProvider clientId="..." authority="..." logPrefix="[Bank Wiki]">
 *   <YourApp />
 * </AzureAuthProvider>
 * ```
 */
export const AzureAuthProvider = ({ 
  children, 
  authService,
  triggerLoginOnMount = false
}: AzureAuthProviderProps) => {
  const authServiceRef = useRef<MsAuthenticationService | null>(null);
  const hasTriggeredAutoLogin = useRef(false);

  useEffect(() => {
    if (!authServiceRef.current) {
      authServiceRef.current = authService;
    }

    const initializeAuth = async () => {
      try {
        if (authServiceRef.current) {
          await authServiceRef.current.initialize();
          
          const account = await authServiceRef.current.getAccount();
          if (account) {
            console.log(`[Chat Auth] User already logged in:`, account.username);
          } else if (triggerLoginOnMount && !hasTriggeredAutoLogin.current) {
            hasTriggeredAutoLogin.current = true;
            console.log(`[Chat Auth] No authenticated user found. Initiating auto-login...`);
            try {
              await authServiceRef.current.login();
              console.log(`[Chat Auth] Auto-login successful`);
            } catch (loginError) {
              console.error(`[Chat Auth] Auto-login failed:`, loginError);
            }
          }
        }
      } catch (error) {
        console.error(`[Chat Auth] Failed to initialize:`, error);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Get the Azure Entra ID access token.
   * This token should be sent as X-DA-Entra-AccessToken header to the backend.
   */
  const getEntraToken = async (): Promise<string | null> => {
    if (!authServiceRef.current) {
      console.error(`[Chat Auth] Auth service not initialized`);
      return null;
    }

    try {
      const account = await authServiceRef.current.getAccount();
      
      if (!account) {
        console.log(`[Chat Auth] User not authenticated`);
        return null;
      }

      const authResult = await authServiceRef.current.login();
      
      if (!authResult || !authResult.accessToken) {
        console.error(`[Chat Auth] Failed to get access token`);
        return null;
      }
      
      return authResult.accessToken;
    } catch (error) {
      console.error(`[Chat Auth] Failed to get Entra token:`, error);
      return null;
    }
  };

  /**
   * Trigger Azure AD login flow.
   */
  const startLogin = async (): Promise<void> => {
    if (!authServiceRef.current) {
      throw new Error(`[Chat Auth] Auth service not initialized`);
    }

    try {
      await authServiceRef.current.login();
      console.log(`[Chat Auth] Login successful`);
    } catch (error) {
      console.error(`[Chat Auth] Login failed:`, error);
      throw error;
    }
  };

  const contextValue: AzureAuthContextType = {
    authService: authServiceRef.current,
    getEntraToken,
    startLogin,
  };

  return (
    <AzureAuthContext.Provider value={contextValue}>
      {children}
    </AzureAuthContext.Provider>
  );
};
